import React, { useState } from "react";
import { captureCurrentWindow } from "../../helpers/captureCurrentWindow";
import { canPickElement } from "../../helpers/captureEnvironment";
import { pickPageElement } from "../../helpers/pickPageElement";
import { describeElement } from "../../helpers/describeElement";
import FeedbackApp from "../../pages/FeedbackGen";
import { TwilioCallDrawer } from "../Twilio";
import { isTokenExpired } from "../../helpers/authToken";
import { getLoggedinUser } from "../../helpers/api_helper";
import {
  isDeveloperUser,
  isFeedbackToolVisible,
} from "../../helpers/functions/feedbackTool";
import "./common.styles.scss";

const FeedbackGenTrigger = () => {
  const [modal, setModal] = useState(false);
  const [dataUrl, setDataUrl] = useState("");
  const [elementInfo, setElementInfo] = useState("");
  const [capturing, setCapturing] = useState(false);
  const [isCallDrawerOpen, setIsCallDrawerOpen] = useState(false);

  const toggle = () => setModal((prev) => !prev);

  const handleGetScreenshotHappen = async () => {
    if (capturing) return;
    setCapturing(true);

    // Step 1 - let the developer point at the element with the issue. Its
    // details are kept aside (NOT shown in the Description box) and appended to
    // the description only on save, so Claude knows where to look. Runs on the
    // live page first so the highlight never lands in the screenshot.
    //
    // Skipped on touch devices: the picker follows a hover cursor, and there
    // isn't one.
    let info = "";
    if (canPickElement()) {
      try {
        const el = await pickPageElement();
        if (el) info = describeElement(el);
      } catch (err) {
        console.error("Element picker failed:", err);
      }
    }
    setElementInfo(info);

    // Step 2 - silent snapshot of the current window (no "choose what to share"
    // prompt). Task 24511: this used to be skipped entirely on any mobile user
    // agent, which is why an iPhone always opened the form with no screenshot
    // and nothing the user pressed could produce one. html2canvas rasterises
    // the DOM and needs no screen-capture API, so it works fine in iOS Safari -
    // there was never a reason to opt phones out. The form still offers a
    // re-capture and a photo upload if this comes back empty.
    try {
      const screenshot = await captureCurrentWindow();
      setDataUrl(screenshot || "");
    } catch (err) {
      console.error("Window capture failed:", err);
      setDataUrl("");
    }

    setCapturing(false);
    setModal(true);
  };

  // Task 24733 - the bug button is no longer developer-only. Everyone signed in
  // gets it unless their company (or they themselves) switched it off; see
  // helpers/functions/feedbackTool.js for how ZCONFIGVAL_56_X resolves.
  //
  // The signed-in check matters because this component is mounted in App.js
  // outside the route guard, so it also renders on /login. The developer flag
  // used to double as that guard (a logged-out visitor has no authUser, so the
  // flag read came back undefined); with the tool on by default it has to be
  // asked explicitly or the login screen grows a bug button.
  //
  // Read without getValidLoggedinUser(): that one CLEARS the stored session as
  // a side effect when the token has expired, which is not something a button's
  // render pass should be doing. Deciding whether to draw an icon only needs
  // the question answered, not the session tidied up.
  const currentUser = getLoggedinUser();
  const isLoggedIn = !!currentUser?.token && !isTokenExpired(currentUser.token);
  const showBugButton = isLoggedIn && isFeedbackToolVisible();

  // The phone drawer is a separate feature that happened to share this frame -
  // it stays behind the developer flag and is NOT part of the rollout.
  const showCallButton = isLoggedIn && isDeveloperUser();

  return (
    <div>
      {modal && (
        <FeedbackApp
          modal={modal}
          toggle={toggle}
          dataUrl={dataUrl}
          elementInfo={elementInfo}
          setModal={setModal}
        />
      )}

      {/* Dialling goes through TwilioCallProvider, so the drawer needs no
          call handler of its own. */}
      <TwilioCallDrawer
        open={isCallDrawerOpen}
        onClose={() => setIsCallDrawerOpen(false)}
      />

      {(showBugButton || showCallButton) && (
        <div className="generate-feedback-button-frame">
          {showBugButton && (
            <div onClick={handleGetScreenshotHappen}>
              <span
                title="Report a problem or suggest an improvement"
                className="generate-feedback-button-frame-bug"
              >
                <i
                  className={
                    capturing
                      ? "fa-solid fa-spinner fa-spin"
                      : "fa-solid fa-bug"
                  }
                ></i>
              </span>
            </div>
          )}

          {showCallButton && (
            <div onClick={() => setIsCallDrawerOpen(true)}>
              <span
                title="Phone / Calls"
                className="generate-feedback-button-frame-call"
              >
                <i className="fa-solid fa-phone"></i>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FeedbackGenTrigger;
